/*
  SAYURI — Discovery Kit cart guard (Phase 1, theme-only mitigation).

  The Discovery Kit is assembled client-side as INDEPENDENT line items: one $25
  "discovery-kit" fee line + N $0 "<scent>-sample" lines, all sharing a unique
  `_kit_group` property (stamped in sayuri-sample-set.build.js buildItems). There
  is no server-side link, so the cart can reach invalid states — an orphaned kit
  with no samples ($25 for nothing), orphaned free samples with no kit, a kit
  held at qty 1 while samples stack, or samples over the cap. This guard runs
  whenever the cart is viewed or changed and repairs those states in ONE
  /cart/update.js call, per `_kit_group`:
    - group has samples but no kit   -> remove the orphaned samples
    - group has a kit but no samples -> remove the orphaned kit
    - valid group -> force exactly one kit line at qty 1, clamp samples to MAX
    - ungrouped $0 sample lines with no kit -> remove (direct-API orphans, on view)

  IMPORTANT: this is MITIGATION, not enforcement. It only runs in the browser
  when the cart is loaded/updated, so it fixes everything a real customer does
  through the drawer / cart page. A determined actor hitting /cart/add.js and
  POSTing straight to /checkout still bypasses it — closing that requires the
  Phase 2 server-side Shopify Function (cart transform + checkout validation).
*/
(function () {
  "use strict";

  var MAX_SAMPLES = 3; // keep in sync with data-max on the sample picker section
  var KIT_HANDLE = "discovery-kit";
  var busy = false;
  var pending = false; // a trigger arrived while a run was in flight → re-run after

  function isKit(item) {
    return item.handle === KIT_HANDLE;
  }
  function isSample(item) {
    // Lines the picker stamped are always kit samples.
    if (item.properties && item.properties._discovery_kit === "yes") return true;
    // The handle-suffix fallback (direct-API orphans) additionally requires a
    // $0 line: this guard exists for FREE sample lines (see header comment) —
    // without the price check it would silently zero a PAID product whose
    // handle merely ends in "-sample" (e.g. the "Discovery Sample" PDP addon).
    if (item.handle && item.handle.length >= 7 && item.handle.slice(-7) === "-sample") {
      var unitPrice = typeof item.original_price === "number" ? item.original_price : item.price;
      return unitPrice === 0;
    }
    return false;
  }
  function groupIdOf(item) {
    return (item.properties && item.properties._kit_group) || "";
  }

  // Returns a map of { lineItemKey: newQuantity } for every line that must change.
  function computeUpdates(cart) {
    var updates = {};
    var groups = {};
    var ungrouped = { kits: [], samples: [], units: 0 };

    (cart.items || []).forEach(function (item) {
      var k = isKit(item);
      var s = isSample(item);
      if (!k && !s) return; // ordinary product — never touched
      var g = groupIdOf(item);
      var bucket = g ? (groups[g] = groups[g] || { kits: [], samples: [], units: 0 }) : ungrouped;
      if (k) {
        bucket.kits.push(item);
      } else {
        bucket.samples.push(item);
        bucket.units += item.quantity;
      }
    });

    function repair(bucket) {
      var hasKit = bucket.kits.length > 0;
      if (!hasKit && bucket.units > 0) {
        bucket.samples.forEach(function (s) { updates[s.key] = 0; }); // orphaned samples
        return;
      }
      if (hasKit && bucket.units === 0) {
        bucket.kits.forEach(function (kt) { updates[kt.key] = 0; }); // orphaned kit
        return;
      }
      if (hasKit && bucket.units > 0) {
        bucket.kits.forEach(function (kt, i) {
          var target = i === 0 ? 1 : 0; // exactly one kit line at qty 1 per group
          if (kt.quantity !== target) updates[kt.key] = target;
        });
        if (bucket.units > MAX_SAMPLES) {
          var remaining = MAX_SAMPLES;
          bucket.samples.forEach(function (s) {
            var keep = Math.min(s.quantity, Math.max(0, remaining));
            remaining -= keep;
            if (keep !== s.quantity) updates[s.key] = keep;
          });
        }
      }
    }

    Object.keys(groups).forEach(function (g) { repair(groups[g]); });
    repair(ungrouped);
    return updates;
  }

  // Reset busy and, if a trigger was dropped while we were running, run again —
  // otherwise a cart change that landed mid-run (e.g. a fast second edit) would
  // never get repaired, leaving an orphaned kit/sample state until the next
  // unrelated cart interaction.
  function finish() {
    busy = false;
    if (pending) { pending = false; run(); }
  }

  function run() {
    if (busy) { pending = true; return; }
    busy = true;
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var updates = computeUpdates(cart);
        if (!Object.keys(updates).length) { finish(); return null; }
        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ updates: updates })
        }).then(function () {
          var core = window.themeCore;
          // Direct /cart/update.js emits nothing, so:
          //  (a) GET_CART -> cart:updated WITH item_count so the HEADER COUNT
          //      BADGE syncs. Without this, removing the last sample makes the
          //      guard delete the orphaned kit but the badge stays at its old
          //      count (e.g. shows "1" on an emptied cart).
          //  (b) cart:refresh -> the drawer / cart page re-renders the repaired
          //      cart. Neither event re-enters this guard into a loop, and the
          //      pending-queue absorbs the follow-up cart:updated idempotently.
          if (core && core.CartApi && typeof core.CartApi.makeRequest === "function") {
            // noOpen: a background repair must sync the badge/sections but never
            // pop the drawer open on its own (e.g. after a back-navigation).
            core.CartApi.makeRequest(core.CartApi.actions.GET_CART, { noOpen: true }).catch(function () {});
          }
          if (core && core.EventBus) {
            core.EventBus.emit("cart:refresh");
          }
          finish();
        });
      })
      .catch(function () { finish(); });
  }

  function init() {
    // Cart page: repair any pre-existing bad state on load. Everywhere else the
    // drawer-open / cart-updated listeners cover it without an extra fetch.
    if (document.querySelector('[data-section-type="cart-template"]')) run();
    window.themeCore.EventBus.listen("cart:updated", run);
    window.themeCore.EventBus.listen("cart:drawer:open", run);
    window.themeCore.EventBus.listen("cart:drawer:refresh-and-open", run);
  }

  // themeCore.EventBus is initialized by the Adastra bundle, which loads AFTER
  // this defer script — so poll until it exists before attaching listeners.
  var tries = 0;
  (function whenReady() {
    if (window.themeCore && window.themeCore.EventBus && typeof window.themeCore.EventBus.listen === "function") {
      init();
      return;
    }
    if (tries++ > 60) return; // give up after ~15s
    setTimeout(whenReady, 250);
  })();
})();
